import { useEffect, useState } from "preact/hooks";
import { t } from "../i18n";
import {
  authEnabled,
  getSession,
  loginAccount,
  registerAccount,
  saveProfile,
  subscribeAuth,
  type AuthSession,
  type SavedResultSummary,
} from "../app/auth";
import type { ProfilePayload } from "../app/profile";
import { GoogleContinue } from "./GoogleContinue";
import { PrivacyNotice } from "./PrivacyNotice";

export function SaveResults({ profile }: { profile: ProfilePayload }) {
  const [session, setSession] = useState<AuthSession>(getSession);
  const [mode, setMode] = useState<"register" | "login">("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState<SavedResultSummary | null>(null);

  useEffect(() => subscribeAuth(setSession), []);

  if (!authEnabled()) return null;

  const onSave = async () => {
    setError("");
    if (!consent) {
      setError(t("account.consent.required"));
      return;
    }
    setBusy(true);
    try {
      if (!getSession().email) {
        if (mode === "register") await registerAccount(email.trim(), password, consent);
        else await loginAccount(email.trim(), password);
      }
      const row = await saveProfile(profile, consent);
      setSaved(row);
      setPassword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("account.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="obs-save-panel" aria-labelledby="obs-save-title">
      <h3 id="obs-save-title">{t("account.save.title")}</h3>
      <p>{session.email ? t("account.save.loggedIn", { email: session.email }) : t("account.save.body")}</p>
      <PrivacyNotice className="obs-privacy-notice obs-privacy-notice--compact" />
      {saved ? (
        <p class="obs-save-ok">
          {t("account.save.done")}{" "}
          <a href={`${saved.printUrl}?autoprint=1`} target="_blank" rel="noreferrer">
            {t("account.downloadPdf")}
          </a>
        </p>
      ) : (
        <form
          class="obs-auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            void onSave();
          }}
        >
          <label class="obs-consent">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent((e.target as HTMLInputElement).checked)}
            />
            <span>
              {t("account.consent")}{" "}
              <a href={session.privacyUrl} target="_blank" rel="noreferrer">
                {t("account.privacyLink")}
              </a>
              .
            </span>
          </label>
          {!session.email && (
            <>
              <GoogleContinue
                consent={consent}
                requireConsent
                disabled={busy}
                onError={setError}
                onSignedIn={() => {
                  void onSave();
                }}
              />
              <div class="obs-auth-modes" role="tablist" aria-label={t("account.mode.label")}>
                <button
                  type="button"
                  class={mode === "register" ? "is-selected" : undefined}
                  aria-pressed={mode === "register"}
                  onClick={() => setMode("register")}
                >
                  {t("account.create")}
                </button>
                <button
                  type="button"
                  class={mode === "login" ? "is-selected" : undefined}
                  aria-pressed={mode === "login"}
                  onClick={() => setMode("login")}
                >
                  {t("account.login")}
                </button>
              </div>
              <label>
                <span>{t("account.email")}</span>
                <input
                  type="email"
                  name="email"
                  autocomplete="username"
                  required
                  value={email}
                  onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                />
              </label>
              <label>
                <span>{t("account.password")}</span>
                <input
                  type="password"
                  name="password"
                  autocomplete={mode === "register" ? "new-password" : "current-password"}
                  minLength={8}
                  required
                  value={password}
                  onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
                />
              </label>
            </>
          )}
          {error ? <p class="obs-form-error" role="alert">{error}</p> : null}
          <p class="obs-wizard-nav">
            <button class="obs-btn obs-btn-primary" type="submit" disabled={busy}>
              {t("account.save.action")}
            </button>
          </p>
        </form>
      )}
    </section>
  );
}
